/**
 * `/claims/:id` (§11.2, §16.2, §16.3): masked detail, external verification result recording (D08 — the platform
 * records, never decides), request-info with precise items, approve (disabled until VERIFIED; dual approval above
 * the tenant threshold; step-up), reject with a safe reason + appeal window, payout destination change (step-up),
 * legal hold, and purpose-bound evidence access for privileged reviewers only.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ClaimField,
  ClaimMachine,
  type ClaimAdminDetailDto,
  type ClaimField as ClaimFieldT,
  type ClaimStatus,
  type VerificationDecision,
} from '@windrop/contracts';
import { Button, Checkbox, Field, Input, Modal, Select, useIntl, useToast } from '@windrop/ui';
import { useState } from 'react';
import { useParams } from 'react-router';
import { Can } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { StepUpCancelled, useStepUp } from '../../auth/step-up';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { MaskedCell } from '../../components/MaskedCell';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge, ToneBadge, humanizeStatus } from '../../components/StatusBadge';
import { Timeline } from '../../components/Timeline';
import { claimsApi } from '../../lib/api/claims';
import { isApiError } from '../../lib/api/client';
import { RISK_LABEL, ageText } from './ClaimsQueuePage';

/** Recordable decisions are also Claim states, so the machine edge `status → decision` is the API's rule. */
const DECISIONS: (VerificationDecision & ClaimStatus)[] = ['VERIFIED', 'MORE_INFO', 'REJECTED'];

/**
 * Decisions the API will accept for a claim in `status`: `VerificationService.applyDecision` runs
 * `ClaimMachine.assertTransition(status, decision)` (Appendix A), so MORE_INFO → VERIFIED, VERIFIED → MORE_INFO and
 * anything from SUBMITTED answer 409 `forbidden_transition`. The console offers only what can succeed (E2E-08).
 */
export const recordableDecisions = (status: ClaimStatus): VerificationDecision[] =>
  DECISIONS.filter((d) => ClaimMachine.canTransition(status, d));

/** Why "Record verification result" is disabled for `status` (undefined when it is enabled). */
export const verificationDisabledReason = (status: ClaimStatus): string | undefined => {
  if (recordableDecisions(status).length > 0) return undefined;
  switch (status) {
    case 'MORE_INFO':
      return 'The claim is waiting for the winner to resubmit; a result can be recorded once it is back in verification (Appendix A).';
    case 'SUBMITTED':
      return 'The claim has not entered verification yet; a result can be recorded once it is VERIFICATION_PENDING.';
    default:
      return 'A result can only be recorded while the claim is under review.';
  }
};

/** Reject is a Claim machine edge (VERIFICATION_PENDING or VERIFIED → REJECTED); elsewhere the API answers 409. */
export const canReject = (status: ClaimStatus): boolean => ClaimMachine.canTransition(status, 'REJECTED');
export const rejectDisabledReason = (status: ClaimStatus): string | undefined => {
  if (canReject(status)) return undefined;
  if (status === 'MORE_INFO')
    return 'The claim is waiting for the winner to resubmit; it can be rejected once it is back in verification.';
  if (status === 'SUBMITTED') return 'The claim has not entered verification yet.';
  return 'Only a claim under review can be rejected.';
};
const REASON_CODES = [
  'DOCUMENT_EXPIRED',
  'DOCUMENT_UNREADABLE',
  'NAME_MISMATCH_UNRESOLVED',
  'UNDERAGE',
  'DUPLICATE_IDENTITY',
  'FRAUD_SUSPECTED',
];

export function ClaimDetailPage() {
  const { id = '' } = useParams();
  const intl = useIntl();
  const qc = useQueryClient();
  const { toast } = useToast();
  const { session, can } = useSession();
  const { withStepUp } = useStepUp();
  const query = useQuery({ queryKey: ['claim', id], queryFn: () => claimsApi.get(id), retry: false });
  type Dialog = 'verification' | 'request-info' | 'reject' | 'payout' | 'legal-hold' | 'evidence';
  const [dialog, setDialog] = useState<null | Dialog>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Opening a dialog starts with a clean error strip; the problem detail of a failed call stays in the dialog. */
  const openDialog = (next: Dialog) => {
    setError(null);
    setDialog(next);
  };
  // verification form
  const [decision, setDecision] = useState<VerificationDecision>('VERIFIED');
  const [providerReference, setProviderReference] = useState('');
  const [reasonCode, setReasonCode] = useState('DOCUMENT_EXPIRED');
  const [note, setNote] = useState('');
  const [manual, setManual] = useState(true);
  // request-info form
  const [field, setField] = useState<ClaimFieldT>('ID_DOCUMENT_IMAGE');
  const [reason, setReason] = useState('');
  const [resubmitDays, setResubmitDays] = useState('7');
  // reject form
  const [safeReason, setSafeReason] = useState('');
  // payout form
  const [bankCode, setBankCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [payoutReason, setPayoutReason] = useState('');
  // legal hold
  const [holdRef, setHoldRef] = useState('');
  const [holdReason, setHoldReason] = useState('');
  // evidence access
  const [evidenceId, setEvidenceId] = useState<string | null>(null);
  const [purpose, setPurpose] = useState('');
  const [evidenceUrl, setEvidenceUrl] = useState<string | null>(null);

  const describeError = (e: unknown, fallback: string): string =>
    isApiError(e)
      ? `${e.problem?.detail ?? e.message}${e.supportReference ? ` (ref ${e.supportReference})` : ''}`
      : fallback;
  const refresh = (dto: ClaimAdminDetailDto) => {
    qc.setQueryData(['claim', id], dto);
    void qc.invalidateQueries({ queryKey: ['claims'] });
  };
  const run = async (fn: () => Promise<ClaimAdminDetailDto>, done: string) => {
    setBusy(true);
    setError(null);
    try {
      refresh(await fn());
      toast({ title: done, tone: 'success' });
      setDialog(null);
    } catch (e) {
      if (e instanceof StepUpCancelled) return;
      setError(describeError(e, 'Something went wrong. Retry in a moment.'));
    } finally {
      setBusy(false);
    }
  };
  const approve = useMutation({
    mutationFn: () =>
      withStepUp(() => claimsApi.approve(id, {}), {
        permission: 'claims.approve',
        reason: `Approving claim ${query.data?.winIdDisplay ?? id}.`,
      }),
    onSuccess: (dto) => {
      refresh(dto);
      toast({
        title:
          dto.status === 'APPROVED'
            ? 'Claim approved'
            : 'First approval recorded — a second reviewer must approve',
        tone: 'success',
      });
    },
    onError: (e) => {
      if (e instanceof StepUpCancelled) return;
      setError(describeError(e, 'Approval failed'));
    },
  });

  if (query.isPending)
    return (
      <PageHeader
        breadcrumb={[{ label: 'Claims queue', to: '/claims' }, { label: 'Loading' }]}
        title="Loading claim"
      />
    );
  if (query.isError || !query.data)
    return (
      <>
        <PageHeader
          breadcrumb={[{ label: 'Claims queue', to: '/claims' }, { label: 'Claim' }]}
          title="Claim"
        />
        {isApiError(query.error) && query.error.status === 404 ? (
          <EmptyState
            title="No claim with this id in your tenant"
            body="It may belong to another organizer; the console never guesses."
            action={<Button href="/claims">Back to the queue</Button>}
          />
        ) : (
          <ErrorState
            title="Claim did not load"
            body="Retry in a moment."
            action={<Button onClick={() => query.refetch()}>Retry</Button>}
          />
        )}
      </>
    );
  const c = query.data;
  const decisions = recordableDecisions(c.status);
  /** Rendered inside every dialog so a refused call (409 problem detail) is never hidden behind the overlay. */
  const dialogError = error ? (
    <p role="alert" className="wd-field__error" data-testid="claim-dialog-error">
      {error}
    </p>
  ) : null;
  const canApprove =
    c.status === 'VERIFIED' &&
    c.verification.decision === 'VERIFIED' &&
    !(c.awaitingSecondApproval && c.approval?.firstApproverId === session?.staffId);
  const approveReason =
    c.status !== 'VERIFIED' || c.verification.decision !== 'VERIFIED'
      ? 'Approval is blocked until the external verification result is VERIFIED (§11.2).'
      : c.awaitingSecondApproval && c.approval?.firstApproverId === session?.staffId
        ? 'You recorded the first approval; a different reviewer must give the second one.'
        : undefined;
  const timelineEvents = c.timeline.map((e, i) => ({
    id: `${e.status}-${i}`,
    at: e.at,
    title: e.text,
    actor: e.owner,
    tone: (e.status === 'REJECTED' || e.status === 'MORE_INFO'
      ? 'warning'
      : e.status === 'APPROVED' || e.status === 'VERIFIED'
        ? 'success'
        : 'neutral') as 'warning' | 'success' | 'neutral',
  }));
  return (
    <>
      <PageHeader
        breadcrumb={[{ label: 'Claims queue', to: '/claims' }, { label: c.winIdDisplay }]}
        title={`${c.winIdDisplay} · ${c.prizeName}`}
        description={`${c.lotteryTitle} · ${humanizeStatus(c.fulfillmentType)} · ${intl.formatMoney(c.prizeValue.amountMinor, c.prizeValue.currency)}${c.requiresDualApproval ? ` · dual approval (threshold ${intl.formatMoney(c.highValueThreshold.amountMinor, c.highValueThreshold.currency)})` : ''}`}
        meta={
          <>
            <StatusBadge kind="ClaimStatus" value={c.status} />
            {c.legalHold ? <ToneBadge tone="warning">Legal hold {c.legalHoldRef}</ToneBadge> : null}
            {c.appealOpen ? <ToneBadge tone="info">Appeal open</ToneBadge> : null}
          </>
        }
        actions={
          <div className="wd-btn-group" data-testid="claim-actions">
            <Can permission="claims.verification.record">
              <Button
                variant="secondary"
                onClick={() => {
                  setDecision(decisions[0] ?? 'VERIFIED');
                  openDialog('verification');
                }}
                disabled={decisions.length === 0}
                disabledReason={verificationDisabledReason(c.status)}
                data-testid="claim-record-verification"
              >
                Record verification result
              </Button>
            </Can>
            <Can permission="claims.request_info">
              <Button
                variant="secondary"
                onClick={() => openDialog('request-info')}
                disabled={!['SUBMITTED', 'VERIFICATION_PENDING'].includes(c.status)}
                disabledReason="Information is requested before the result is recorded; a verified claim is approved or rejected"
              >
                Request information
              </Button>
            </Can>
            <Can permission="claims.approve">
              <Button
                variant="primary"
                onClick={() => approve.mutate()}
                disabled={!canApprove || approve.isPending}
                disabledReason={approveReason}
                loading={approve.isPending}
                loadingLabel="Approving"
                data-testid="claim-approve"
              >
                {c.requiresDualApproval && c.awaitingSecondApproval
                  ? 'Give second approval'
                  : c.requiresDualApproval
                    ? 'Approve (1 of 2)'
                    : 'Approve'}
              </Button>
            </Can>
            <Can permission="claims.reject">
              <Button
                variant="danger"
                onClick={() => openDialog('reject')}
                disabled={!canReject(c.status)}
                disabledReason={rejectDisabledReason(c.status)}
                data-testid="claim-reject"
              >
                Reject
              </Button>
            </Can>
            <Can permission="claims.appeal.decide">
              {c.appealOpen ? (
                <>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      void run(
                        () =>
                          claimsApi.decideAppeal(id, {
                            decision: 'REOPEN',
                            note: 'Review reopened after appeal.',
                          }),
                        'Appeal accepted — review reopened',
                      )
                    }
                  >
                    Reopen review
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() =>
                      void run(
                        () =>
                          claimsApi.decideAppeal(id, {
                            decision: 'UPHOLD',
                            note: 'Rejection upheld after appeal.',
                          }),
                        'Rejection upheld',
                      )
                    }
                  >
                    Uphold rejection
                  </Button>
                </>
              ) : null}
            </Can>
            <Can permission="claims.review">
              {c.fulfillmentType === 'CASH' ? (
                <Button variant="secondary" onClick={() => openDialog('payout')}>
                  Change payout destination
                </Button>
              ) : null}
              <Button variant="secondary" onClick={() => openDialog('legal-hold')}>
                {c.legalHold ? 'Clear legal hold' : 'Set legal hold'}
              </Button>
            </Can>
          </div>
        }
      />
      {error && !dialog ? (
        <p role="alert" className="wd-surface" style={{ padding: 'var(--wd-space-3)' }}>
          {error}
        </p>
      ) : null}
      <div className="wdc-grid">
        <section
          className="wd-surface"
          style={{ padding: 'var(--wd-space-4)' }}
          data-testid="claim-review-facts"
        >
          <h2>Review</h2>
          <dl className="wdc-facts">
            <div>
              <dt>Age</dt>
              <dd>
                {ageText(c.ageSeconds)} (opened {intl.formatDateTime(c.openedAt)}
                {c.submittedAt ? `, submitted ${intl.formatDateTime(c.submittedAt)}` : ''})
              </dd>
            </div>
            <div>
              <dt>Claim deadline</dt>
              <dd>
                {intl.formatDateTime(c.claimDeadline)}
                {c.deadlineExtendedTo ? ' (extended — platform-caused delay)' : ''}
              </dd>
            </div>
            <div>
              <dt>External verification</dt>
              <dd>
                {c.verificationCase ? (
                  <>
                    <StatusBadge kind="VerificationDecision" value={c.verificationCase.decision} />{' '}
                    {c.verificationCase.provider}
                    {c.verificationCase.providerReference
                      ? ` · ${c.verificationCase.providerReference}`
                      : ' · no provider reference (deferred)'}
                    {c.verificationCase.reasonCodes.length
                      ? ` · ${c.verificationCase.reasonCodes.join(', ')}`
                      : ''}
                    {c.verificationCase.manualFallback ? ' · manual fallback' : ''}
                  </>
                ) : (
                  'Not yet handed off'
                )}
              </dd>
            </div>
            <div>
              <dt>Missing information</dt>
              <dd>
                {c.missingItems.length ? (
                  <ul>
                    {c.missingItems.map((m) => (
                      <li key={m.field}>
                        {humanizeStatus(m.field)}: {m.reason}
                      </li>
                    ))}
                  </ul>
                ) : (
                  '—'
                )}
              </dd>
            </div>
            <div>
              <dt>Risk flags</dt>
              <dd>
                {c.riskFlags.length ? (
                  <ul>
                    {c.riskFlags.map((f) => (
                      <li key={f}>{RISK_LABEL[f] ?? f}</li>
                    ))}
                  </ul>
                ) : (
                  'none'
                )}
              </dd>
            </div>
            <div>
              <dt>Hold</dt>
              <dd>
                {c.hold
                  ? `${c.hold.variantSummary.sku} until ${intl.formatDateTime(c.hold.expiresAt)}${c.hold.extensionsUsed ? ' (extended once)' : ''}`
                  : c.selectedVariant
                    ? `${c.selectedVariant.sku} · ${intl.formatMoney(c.selectedVariant.value.amountMinor, c.selectedVariant.value.currency)} (converted)`
                    : '—'}
              </dd>
            </div>
            <div>
              <dt>Preference</dt>
              <dd>
                {c.preferenceSummary ?? '—'} · {c.chosenPlatform ?? 'no platform yet'}
              </dd>
            </div>
            <div>
              <dt>Approval</dt>
              <dd>
                {c.approval?.approvedBy
                  ? `Approved by ${c.approval.approvedBy}`
                  : c.awaitingSecondApproval
                    ? `First approval by ${c.approval?.firstApproverId} — awaiting a second reviewer`
                    : '—'}
              </dd>
            </div>
            <div>
              <dt>Reviewer note</dt>
              <dd>{c.reviewNote ?? '—'}</dd>
            </div>
            {c.appeal ? (
              <div>
                <dt>Appeal</dt>
                <dd>
                  {intl.formatDateTime(c.appeal.at)}: “{c.appeal.note}”{' '}
                  {c.appeal.decision ? `→ ${c.appeal.decision}` : '(open)'}
                </dd>
              </div>
            ) : null}
            {c.statusReason ? (
              <div>
                <dt>Participant-safe reason</dt>
                <dd>{c.statusReason}</dd>
              </div>
            ) : null}
          </dl>
        </section>
        <section className="wd-surface" style={{ padding: 'var(--wd-space-4)' }} data-testid="claim-identity">
          <h2>Identity and destination (masked)</h2>
          <dl className="wdc-facts">
            <div>
              <dt>Legal name</dt>
              <dd>
                <MaskedCell
                  masked={c.legalNameMasked}
                  what="legal name"
                  subject={{ kind: 'CLAIM_IDENTITY', claimId: c.claimId }}
                />
              </dd>
            </div>
            {c.payoutDestination ? (
              <div>
                <dt>Payout destination</dt>
                <dd>
                  <MaskedCell
                    masked={c.payoutDestination.masked}
                    what="payout destination"
                    subject={{ kind: 'PAYOUT_DESTINATION', payoutId: c.payoutDestination.payoutId }}
                  />{' '}
                  {c.payoutDestination.verifiedAt ? null : (
                    <ToneBadge tone="warning">Awaiting second review</ToneBadge>
                  )}
                </dd>
              </div>
            ) : null}
          </dl>
          <h3>Evidence</h3>
          <ul data-testid="claim-evidence">
            {c.evidence.map((e) => (
              <li key={e.evidenceId}>
                {humanizeStatus(e.kind)} · {e.mimeType ?? 'identity record'} ·{' '}
                {e.purged ? 'purged' : e.purgeAt ? `purge ${intl.formatDate(e.purgeAt)}` : e.retentionClass}
                {e.idNumberMasked ? ` · ID ${e.idNumberMasked}` : ''}
                {e.kind !== 'legal_identity' && !e.purged ? (
                  <Can
                    permission="identity.evidence.read"
                    fallback={<span className="wd-muted"> · privileged reviewers only</span>}
                  >
                    {' '}
                    <Button
                      variant="quiet"
                      onClick={() => {
                        setEvidenceId(e.evidenceId);
                        setEvidenceUrl(null);
                        openDialog('evidence');
                      }}
                    >
                      Open (audited)
                    </Button>
                  </Can>
                ) : null}
              </li>
            ))}
          </ul>
          {c.fulfillment ? (
            <>
              <h3>Fulfillment</h3>
              <p>
                <StatusBadge kind="FulfillmentStatus" value={c.fulfillment.status} /> {c.fulfillment.owner}
                {c.fulfillment.slaDueAt ? ` · due ${intl.formatDateTime(c.fulfillment.slaDueAt)}` : ''} ·{' '}
                <a href="/fulfillment">Fulfillment console</a>
              </p>
            </>
          ) : null}
        </section>
      </div>
      <section className="wd-surface" style={{ padding: 'var(--wd-space-4)' }}>
        <h2>Timeline</h2>
        <Timeline events={timelineEvents} />
      </section>

      <Modal
        open={dialog === 'verification'}
        onOpenChange={(o) => !o && setDialog(null)}
        title="Record the external verification result"
        description="The platform records the client's verification decision (D08/D16); it never decides identity itself."
        actions={
          <Button
            variant="primary"
            loading={busy}
            onClick={() =>
              void run(
                () =>
                  claimsApi.recordVerification(id, {
                    decision,
                    providerReference: providerReference.trim(),
                    reasonCodes: decision === 'VERIFIED' ? [] : [reasonCode],
                    manualFallback: manual,
                    ...(note.trim() ? { note: note.trim() } : {}),
                  }),
                'Verification result recorded',
              )
            }
            disabled={!providerReference.trim()}
          >
            Record
          </Button>
        }
      >
        {dialogError}
        <Field label="Decision" required>
          <Select
            value={decision}
            onChange={(e) => setDecision(e.target.value as VerificationDecision)}
            options={decisions.map((d) => ({ value: d, label: humanizeStatus(d) }))}
          />
        </Field>
        <Field label="Provider reference / export batch id" required>
          <Input value={providerReference} onChange={(e) => setProviderReference(e.target.value)} />
        </Field>
        {decision !== 'VERIFIED' ? (
          <Field label="Reason code" required>
            <Select
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              options={REASON_CODES.map((r) => ({ value: r, label: humanizeStatus(r) }))}
            />
          </Field>
        ) : null}
        <Field label="Note (internal)">
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <Checkbox
          label="Recorded from the signed manual-review export"
          checked={manual}
          onCheckedChange={setManual}
        />
      </Modal>

      <Modal
        open={dialog === 'request-info'}
        onOpenChange={(o) => !o && setDialog(null)}
        title="Request precise missing information"
        description="Lists exactly what the winner must replace; everything else they sent is preserved and the deadline is extended."
        actions={
          <Button
            variant="primary"
            loading={busy}
            disabled={reason.trim().length < 3}
            onClick={() =>
              void run(
                () =>
                  claimsApi.requestInfo(id, {
                    items: [{ field, reason: reason.trim() }],
                    resubmissionDeadline: new Date(
                      Date.now() + Number(resubmitDays) * 86_400_000,
                    ).toISOString(),
                  }),
                'Information requested',
              )
            }
          >
            Send request
          </Button>
        }
      >
        {dialogError}
        <Field label="Item" required>
          <Select
            value={field}
            onChange={(e) => setField(e.target.value as ClaimFieldT)}
            options={ClaimField.options.map((f) => ({ value: f, label: humanizeStatus(f) }))}
          />
        </Field>
        <Field label="What is wrong with it (participant-safe)" required>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <Field label="Resubmission window (days)" required>
          <Input
            type="number"
            min={1}
            max={30}
            value={resubmitDays}
            onChange={(e) => setResubmitDays(e.target.value)}
          />
        </Field>
      </Modal>

      <Modal
        open={dialog === 'reject'}
        onOpenChange={(o) => !o && setDialog(null)}
        title="Reject this claim"
        description="The winner sees only the participant-safe reason and an appeal route; the redraw policy applies after the appeal window."
        actions={
          <Button
            variant="danger"
            loading={busy}
            disabled={safeReason.trim().length < 3}
            onClick={() =>
              void run(
                () =>
                  claimsApi.reject(id, {
                    reasonCode,
                    safeReason: safeReason.trim(),
                    ...(note.trim() ? { internalNote: note.trim() } : {}),
                  }),
                'Claim rejected — appeal window open',
              )
            }
          >
            Reject
          </Button>
        }
      >
        {dialogError}
        <Field label="Reason code" required>
          <Select
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value)}
            options={REASON_CODES.map((r) => ({ value: r, label: humanizeStatus(r) }))}
          />
        </Field>
        <Field
          label="Participant-safe reason"
          hint='Completes "Your claim was not approved because …"'
          required
        >
          <Input value={safeReason} onChange={(e) => setSafeReason(e.target.value)} />
        </Field>
        <Field label="Internal note">
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </Modal>

      <Modal
        open={dialog === 'payout'}
        onOpenChange={(o) => !o && setDialog(null)}
        title="Change the payout destination"
        description="Step-up required; the previous destination is invalidated; high-value changes wait for a second reviewer (Appendix A)."
        actions={
          <Button
            variant="primary"
            loading={busy}
            disabled={
              !bankCode.trim() ||
              accountNumber.trim().length < 4 ||
              !accountName.trim() ||
              payoutReason.trim().length < 5
            }
            onClick={() =>
              void run(
                () =>
                  withStepUp(
                    () =>
                      claimsApi.payoutDestination(id, {
                        destination: {
                          kind: 'BANK',
                          bankCode: bankCode.trim(),
                          accountNumber: accountNumber.trim(),
                          accountName: accountName.trim(),
                        },
                        reason: payoutReason.trim(),
                      }),
                    { permission: 'claims.review', reason: 'Changing a payout destination.' },
                  ),
                'Payout destination changed',
              )
            }
          >
            Change
          </Button>
        }
      >
        {dialogError}
        <Field label="Bank code" required>
          <Input value={bankCode} onChange={(e) => setBankCode(e.target.value)} />
        </Field>
        <Field label="Account number" required>
          <Input
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            autoComplete="off"
          />
        </Field>
        <Field label="Account name" required>
          <Input value={accountName} onChange={(e) => setAccountName(e.target.value)} />
        </Field>
        <Field label="Reason (audited)" required>
          <Input value={payoutReason} onChange={(e) => setPayoutReason(e.target.value)} />
        </Field>
      </Modal>

      <Modal
        open={dialog === 'legal-hold'}
        onOpenChange={(o) => !o && setDialog(null)}
        title={c.legalHold ? 'Clear the legal hold' : 'Set a legal hold'}
        description="While a legal hold applies, evidence is never purged (§17.3)."
        actions={
          <Button
            variant="primary"
            loading={busy}
            disabled={holdRef.trim().length < 3 || holdReason.trim().length < 5}
            onClick={() =>
              void run(
                () =>
                  withStepUp(
                    () =>
                      claimsApi.legalHold(id, {
                        enabled: !c.legalHold,
                        reference: holdRef.trim(),
                        reason: holdReason.trim(),
                      }),
                    { permission: 'claims.review', reason: 'Legal hold change.' },
                  ),
                c.legalHold ? 'Legal hold cleared' : 'Legal hold set',
              )
            }
          >
            {c.legalHold ? 'Clear' : 'Set hold'}
          </Button>
        }
      >
        {dialogError}
        <Field label="Case / matter reference" required>
          <Input value={holdRef} onChange={(e) => setHoldRef(e.target.value)} />
        </Field>
        <Field label="Reason (audited)" required>
          <Input value={holdReason} onChange={(e) => setHoldReason(e.target.value)} />
        </Field>
      </Modal>

      <Modal
        open={dialog === 'evidence'}
        onOpenChange={(o) => !o && setDialog(null)}
        title="Open evidence (audited)"
        description="Purpose is written to the audit trail; the link is short-lived and watermarked where supported."
        actions={
          evidenceUrl ? (
            <Button href={evidenceUrl} variant="primary" target="_blank" rel="noopener">
              Open document
            </Button>
          ) : (
            <Button
              variant="primary"
              loading={busy}
              disabled={purpose.trim().length < 10}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  const r = await withStepUp(
                    () => claimsApi.evidenceAccess(id, evidenceId!, purpose.trim()),
                    { permission: 'identity.evidence.read', reason: 'Opening identity evidence.' },
                  );
                  setEvidenceUrl(r.url);
                } catch (e) {
                  if (!(e instanceof StepUpCancelled)) setError(describeError(e, 'Access failed'));
                } finally {
                  setBusy(false);
                }
              }}
            >
              Request access
            </Button>
          )
        }
      >
        {dialogError}
        <Field label="Purpose (at least 10 characters)" required>
          <Input value={purpose} onChange={(e) => setPurpose(e.target.value)} />
        </Field>
        {!can('identity.evidence.read') ? (
          <p className="wd-muted">Only privileged reviewers can open evidence.</p>
        ) : null}
      </Modal>
    </>
  );
}
